import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Skeleton, Alert, IconButton,
  Tooltip, LinearProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getIotDevices } from '../../utils/clientUtils';

export default function IotDevices() {
  const { t, i18n } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAuthSession().then(session => {
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
      setIsAdmin(groups.includes('device_management'));
      setAdminChecked(true);
    });
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    const data = await getIotDevices();
    if (Array.isArray(data)) {
      setDevices(data);
    } else {
      setError(t('iotDevices.loadError'));
      setDevices([]);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (!isAdmin) return;
    loadDevices();
  }, [isAdmin, loadDevices]);

  if (!adminChecked) return null;

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h2" sx={{ color: 'primary.main', textAlign: 'left' }}>{t('iotDevices.title')}</Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>{t('iotDevices.accessDenied')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h2" sx={{ color: 'primary.main', textAlign: 'left', flex: 1 }}>{t('iotDevices.title')}</Typography>
        <Tooltip title={t('iotDevices.refresh')}>
          <span>
            <IconButton onClick={loadDevices} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('iotDevices.description')}</Typography>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('iotDevices.thingName')}</TableCell>
              <TableCell>{t('iotDevices.type')}</TableCell>
              <TableCell>{t('iotDevices.lastSeen')}</TableCell>
              <TableCell>{t('iotDevices.attributes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && devices.length === 0
              ? Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              : devices.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                      {t('iotDevices.noDevices')}
                    </TableCell>
                  </TableRow>
                )
                : devices.map(d => (
                  <TableRow key={d.thingName}>
                    <TableCell>{d.thingName}</TableCell>
                    <TableCell>{d.thingType ?? '—'}</TableCell>
                    <TableCell>
                      {d.lastSeen ? new Date(d.lastSeen * 1000).toLocaleString(i18n.language) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {d.attributes
                        ? Object.entries(d.attributes).map(([k, v]) => `${k}=${v}`).join(', ')
                        : '—'
                      }
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
